///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SuiTypeFile, SuiTypeString } from '@siklu/sui-lib/infrastructure';
import { finalize } from 'rxjs';
import { GtthEventManager } from '../../../services/event-manager.service';
import { LoaderIds, PreloaderService } from '../../../services/preloader.service';
import { ProjectFilesService } from '../../../services/project/project-files.service';
import { ProjectDTO, ProjectFolderEnum } from '../../../generated/shared-types.model';
import { PARAM_TREE_ID } from '../../../shared/constants/url-params.constants';

@Component({
    selector: 'winde-project-import-dialog',
    templateUrl: './project-files-import-dialog.component.html',
    styles: `
        .form-label {
            display: inline-block;
            margin-bottom: 0.5rem;
        }

        .siklu-mat-dialog-footer {
            justify-content: space-between;
        }
    `,
    standalone: false
})
export class ProjectFilesImportDialogComponent implements OnInit {
    protected readonly loaderKey = LoaderIds.PROJECT_IMPORT_DIALOG_PRELOADER_ID;
    protected name: SuiTypeString;
    protected description: SuiTypeString;
    protected file: SuiTypeFile;
    protected acceptedMimeTypes = '.winde';
    protected shouldOpenProject = true;

    constructor(
        private readonly router: Router,
        private readonly eventManager: GtthEventManager,
        private readonly projectFilesService: ProjectFilesService,
        public readonly preloaderService: PreloaderService,
        public dialogRef: MatDialogRef<ProjectFilesImportDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { parentId: number }
    ) {}

    @HostListener('document:keydown', ['$event'])
    handleKeyboardUpEvent(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            this.import();
        }
    }

    ngOnInit(): void {
        this.initForm();
    }

    import() {
        this.name.validateAndShowError();
        this.file.validateAndShowError();

        if (this.name.invalid() || this.file.invalid() || !this.file.getValue()) {
            return;
        }

        const parentId: number = this.data.parentId;
        const name: string = this.name.getValue();
        const description: string = this.description.getValue();
        const file: File = this.file.getValue();

        this.preloaderService.show(this.loaderKey);
        this.projectFilesService
            .importProjectInterchangeArchive(parentId, name, description, file)
            .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
            .subscribe((project) => this.onSaveSuccess(project));
    }

    private initForm() {
        this.name = new SuiTypeString('', [/^[a-zA-Z0-9 ]*$/g], 1, 255);
        this.name.isMandatory = true;
        this.name.validationMessageParams.isEnabled = true;
        this.name.validationMessageParams.isActivated = false;

        this.description = new SuiTypeString('', [], 0, 255);
        this.description.isMandatory = false;
        this.description.validationMessageParams.isEnabled = false;
        this.description.validationMessageParams.isActivated = false;

        this.file = new SuiTypeFile();
        this.file.mimeTypes = this.acceptedMimeTypes;
        this.file.isMandatory = true;
        this.file.validationMessageParams.isEnabled = true;
        this.file.validationMessageParams.isActivated = false;
    }

    private onSaveSuccess(project: ProjectDTO) {
        this.dialogRef.close();
        this.eventManager.broadcast({ name: 'projectModified', content: 'OK' });
        if (this.shouldOpenProject) {
            setTimeout(() => {
                this.router.navigate(['/project-view', project.id], { queryParams: { [PARAM_TREE_ID]: project.parentId } });
            }, 0);
        }
    }
}

@Component({
    selector: 'winde-import-project-popup',
    template: '',
    standalone: false
})
export class ProjectImportPopupComponent implements OnInit {
    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly dialog: MatDialog
    ) {}

    ngOnInit() {
        const params = this.route.snapshot.paramMap;
        const rootFolderIds = this.route.snapshot.data.rootFolderIds;
        const parentId = params.get('parentId');
        const dialogRef = this.dialog.open(ProjectFilesImportDialogComponent, {
            width: '750px',
            position: {
                top: '30px'
            },
            data: {
                parentId: parentId ? parseInt(parentId, 10) : rootFolderIds[ProjectFolderEnum.BOM_CONFIG]
            },
            closeOnNavigation: true
        });

        dialogRef.afterClosed().subscribe(() => {
            this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
        });
    }
}
